/*
 * @override KINDW-328 — GT-010 consent on CMP choice.
 * useDntNotification is inlined (retail re-export from this path is circular).
 */
import React, {useEffect, useMemo, useRef} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useLocation} from 'react-router-dom';
import PropTypes from 'prop-types';

import {useDNT} from '@salesforce/commerce-sdk-react';
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive';
import {
  Box, Button, Flex, Heading, Modal, ModalBody, ModalCloseButton,
  ModalContent, Stack, Text, useDisclosure,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {applyCmpConsentChoice} from '@amblique/king-living/app/utils/gtm-consent';
import {getGtmConfigFromSite} from '@amblique/king-living/app/utils/gtm-utils';

export const DntNotification = ({isOpen, onOpen, onClose}) => {
  const {selectedDnt, updateDNT} = useDNT();
  const {site} = useMultiSite();
  const gtmEnabled = useMemo(() => getGtmConfigFromSite(site).enabled, [site]);
  const {formatMessage} = useIntl();
  const location = useLocation();
  const choiceSubmittedRef = useRef(false);

  useEffect(() => {
    if (choiceSubmittedRef.current) {
      return;
    }

    if (selectedDnt === undefined) {
      onOpen();
    } else {
      onClose();
    }
  }, [location, onClose, onOpen, selectedDnt]);

  const applyCmpConsent = (analytics, marketing) => {
    applyCmpConsentChoice({analytics, marketing}, site?.id, gtmEnabled);
  };

  const onCloseNotification = async () => {
    choiceSubmittedRef.current = true;
    applyCmpConsent(true, false);
    await updateDNT(null);
    onClose();
  };

  const buttons = (
    <>
      <Button
        _hover={{bg: 'gray.100'}}
        bg="white"
        border="1px"
        borderColor="gray.100"
        boxShadow="md"
        color="black"
        width="100%"
        aria-label={formatMessage({
          id: 'dnt_notification.button.assistive_msg.decline',
          defaultMessage: 'Decline tracking',
        })}
        onClick={async () => {
          choiceSubmittedRef.current = true;
          applyCmpConsent(false, false);
          await updateDNT(true);
          onClose();
        }}
      >
        <FormattedMessage defaultMessage="Decline" id="dnt_notification.button.decline" />
      </Button>
      <Button
        boxShadow="md"
        width="100%"
        aria-label={formatMessage({
          id: 'dnt_notification.button.assistive_msg.accept',
          defaultMessage: 'Accept tracking',
        })}
        onClick={async () => {
          choiceSubmittedRef.current = true;
          applyCmpConsent(true, true);
          await updateDNT(false);
          onClose();
        }}
      >
        <FormattedMessage defaultMessage="Accept" id="dnt_notification.button.accept" />
      </Button>
    </>
  );

  const description = (
    <Text color="gray.700" fontWeight={500} marginTop={7}>
      <FormattedMessage
        defaultMessage="We use cookies and similar technologies to improve your experience, understand how our site is used, and support marketing. You can accept or decline optional tracking at any time."
        id="dnt_notification.description"
      />
    </Text>
  );

  return (
    <Modal
      blockScrollOnMount={false}
      closeOnOverlayClick={false}
      data-testid="sf-dnt-notification"
      isOpen={isOpen}
      size="sm"
      trapFocus={false}
      onClose={onCloseNotification}
      onOpen={onOpen}
    >
      <ModalContent
        borderTopRadius="md"
        bottom={0}
        left={0}
        margin="0"
        maxWidth="100%"
        pointerEvents="all"
        position="absolute"
        right={0}
        width="100%"
        containerProps={{
          pointerEvents: 'none',
        }}
      >
        <Box boxShadow="0 12px 48px rgba(0, 0, 0, 0.3)">
          <ModalCloseButton
            aria-label={formatMessage({
              id: 'dnt_notification.button.assistive_msg.close',
              defaultMessage: 'Close consent tracking form',
            })}
          />
          <ModalBody bg="white" marginTop={7} paddingBottom={14} pb={8}>
            <Heading as="h3" fontSize={25} width="100%">
              <FormattedMessage
                defaultMessage="Tracking Consent"
                id="dnt_notification.title"
              />
            </Heading>
            <HideOnDesktop>
              <Flex direction="column">
                {description}
                <Stack align="flex-end" direction="column" mt={4} spacing={4}>
                  {buttons}
                </Stack>
              </Flex>
            </HideOnDesktop>
            <HideOnMobile>
              <Flex align="center">
                {description}
                <Stack
                  align="flex-end"
                  direction="row"
                  marginLeft={6}
                  mt={4}
                  spacing={4}
                >
                  {buttons}
                </Stack>
              </Flex>
            </HideOnMobile>
          </ModalBody>
        </Box>
      </ModalContent>
    </Modal>
  );
};

DntNotification.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export const useDntNotification = () => {
  const {isOpen, onOpen, onClose} = useDisclosure();

  return {
    isOpen,
    onOpen,
    onClose,
  };
};
